import {
  Box3,
  MathUtils,
  Object3D,
  PerspectiveCamera,
  SkinnedMesh,
  Vector3,
} from 'three';

import type { PlayerCameraControlsHandle } from '../types';

function isFiniteVector3(v: Vector3): boolean {
  return Number.isFinite(v.x) && Number.isFinite(v.y) && Number.isFinite(v.z);
}

function isUsableBounds(box: Box3): boolean {
  if (box.isEmpty()) return false;
  const size = box.getSize(new Vector3());
  const center = box.getCenter(new Vector3());
  if (!isFiniteVector3(size) || !isFiniteVector3(center)) return false;
  return size.lengthSq() > 1e-12;
}

/**
 * World-space bounds for framing. Uses the full hierarchy (`setFromObject`) so Z-up meshes
 * and helpers are included, then unions precise skinned bounds when available so nothing is
 * clipped by too-tight geometry boxes.
 */
export function boundsForPlayerCharacter(root: Object3D): Box3 {
  root.updateMatrixWorld(true);

  const hull = new Box3().setFromObject(root, true);
  if (!isUsableBounds(hull)) {
    return new Box3().setFromObject(root);
  }

  const skinnedMeshBox = new Box3();
  let hasSkinnedMesh = false;

  root.traverse((object) => {
    if (!(object instanceof SkinnedMesh)) return;
    skinnedMeshBox.expandByObject(object, true);
    hasSkinnedMesh = true;
  });

  if (hasSkinnedMesh && isUsableBounds(skinnedMeshBox)) {
    hull.union(skinnedMeshBox);
  }

  return hull;
}

/**
 * Camera distance so a world-space bounding sphere fits in both vertical and horizontal FOV.
 * Works for any character orientation (Z-up vs Y-up); axis-aligned box fitting alone does not.
 */
export function fitBoundingSpherePerspectiveDistance(
  camera: PerspectiveCamera,
  radius: number,
): number {
  const safeR = Math.max(radius, 1e-6);
  const vFovRad = camera.getEffectiveFOV() * MathUtils.DEG2RAD;
  const aspect = Math.max(camera.aspect, 1e-6);
  const hFovRad = 2 * Math.atan(Math.tan(vFovRad * 0.5) * aspect);
  const dVert = safeR / Math.tan(vFovRad * 0.5);
  const dHoriz = safeR / Math.tan(hFovRad * 0.5);
  const distance = Math.max(dVert, dHoriz);
  return Number.isFinite(distance) && distance > 0 ? distance : 10;
}

export function fitBoxPerspectiveDistance(
  camera: PerspectiveCamera,
  width: number,
  height: number,
  depth: number,
): number {
  const safeHeight = Math.max(height, 1e-6);
  const fovRad = camera.getEffectiveFOV() * MathUtils.DEG2RAD;
  const cameraAspect = Math.max(camera.aspect, 1e-6);
  const boxAspect = width / safeHeight;
  const fitSize = boxAspect < cameraAspect ? safeHeight : width / cameraAspect;
  const distance =
    (fitSize * 0.5) / Math.tan(fovRad * 0.5) + Math.max(depth, 0) * 0.5;
  return Number.isFinite(distance) && distance > 0 ? distance : 10;
}

export interface FitPlayerCameraToCharacterOptions {
  /**
   * Fraction of the mesh bounding-box height added to the top and bottom
   * before fitting (e.g. 0.1 = 10% padding above and below).
   */
  verticalPadding?: number;
  /** Additional upward bias applied to the final look target. */
  targetYBiasFactor?: number;
  /**
   * Optional camera-to-target offset direction used for framing.
   * When omitted, framing derives the direction from the current camera pose.
   */
  viewDirection?: Vector3;
  /** Framing distance strategy. Defaults to sphere to preserve existing player behavior. */
  fitStrategy?: 'sphere' | 'box';
  /** Applied to the computed camera distance (default 1). */
  distanceMultiplier?: number;
  enableTransition?: boolean;
}

export interface FitPlayerCameraPose {
  position: Vector3;
  target: Vector3;
  /** World-space radius of the padded bounding sphere used to compute camera distance. */
  framingSphereRadius: number;
}

const MIN_NEAR = 0.01;
const FAR_SLACK = 3500;

function getBoxFramingRadius(box: Box3, target: Vector3): number {
  const { min, max } = box;
  const corners = [
    new Vector3(min.x, min.y, min.z),
    new Vector3(min.x, min.y, max.z),
    new Vector3(min.x, max.y, min.z),
    new Vector3(min.x, max.y, max.z),
    new Vector3(max.x, min.y, min.z),
    new Vector3(max.x, min.y, max.z),
    new Vector3(max.x, max.y, min.z),
    new Vector3(max.x, max.y, max.z),
  ];

  let maxDistanceSq = 0;
  for (const corner of corners) {
    maxDistanceSq = Math.max(maxDistanceSq, target.distanceToSquared(corner));
  }

  const radius = Math.sqrt(maxDistanceSq);
  return Number.isFinite(radius) && radius > 0 ? radius : 1;
}

export function getFitPlayerCameraPoseForBox(
  camera: PerspectiveCamera,
  box: Box3,
  options?: FitPlayerCameraToCharacterOptions,
): FitPlayerCameraPose | null {
  const verticalPadding = options?.verticalPadding ?? 0.1;
  const targetYBiasFactor = options?.targetYBiasFactor ?? 0;
  const fitStrategy = options?.fitStrategy ?? 'sphere';
  const distanceMultiplier = options?.distanceMultiplier ?? 1;

  if (!isUsableBounds(box)) return null;

  const boxSize = box.getSize(new Vector3());
  const paddedBox = box.clone();
  const dy = boxSize.y * verticalPadding;
  paddedBox.min.y -= dy;
  paddedBox.max.y += dy;

  const paddedSize = paddedBox.getSize(new Vector3());
  const target = paddedBox.getCenter(new Vector3());
  target.y += paddedSize.y * targetYBiasFactor;
  if (!isFiniteVector3(target) || !isFiniteVector3(paddedSize)) return null;

  const framingSphereRadius = getBoxFramingRadius(paddedBox, target);
  const framingWidth =
    Math.max(target.x - paddedBox.min.x, paddedBox.max.x - target.x) * 2;
  const framingHeight =
    Math.max(target.y - paddedBox.min.y, paddedBox.max.y - target.y) * 2;
  const framingDepth =
    Math.max(target.z - paddedBox.min.z, paddedBox.max.z - target.z) * 2;
  const baseDistance =
    fitStrategy === 'box'
      ? fitBoxPerspectiveDistance(
          camera,
          framingWidth,
          framingHeight,
          framingDepth,
        )
      : fitBoundingSpherePerspectiveDistance(camera, framingSphereRadius);
  const distance = baseDistance * distanceMultiplier;

  const viewDirection =
    options?.viewDirection?.clone() ?? camera.position.clone().sub(target);
  if (!isFiniteVector3(viewDirection) || viewDirection.lengthSq() === 0) {
    viewDirection.set(0, 0, 1);
  } else {
    viewDirection.normalize();
  }

  const position = target.clone().add(viewDirection.multiplyScalar(distance));
  if (!isFiniteVector3(position)) return null;

  return { position, target, framingSphereRadius };
}

/**
 * Adjusts perspective clipping so framed subjects and the default backdrop stay visible
 * when the camera moves back for large meshes (default three.js far is often 2000).
 */
export function applyPlayerPerspectiveClippingForFraming(
  camera: PerspectiveCamera,
  cameraPosition: Vector3,
  target: Vector3,
  framingSphereRadius: number,
): void {
  const dist = cameraPosition.distanceTo(target);
  const r = Math.max(framingSphereRadius, 1e-6);

  const nearFromDistance = Math.max(dist * 1e-4, MIN_NEAR);
  const maxNearBeforeSubjectClip = Math.max((dist - r) * 0.25, MIN_NEAR);
  const near = Math.min(nearFromDistance, maxNearBeforeSubjectClip);

  const far = Math.max(2000, dist + r * 8 + FAR_SLACK, dist * 1.25 + FAR_SLACK);

  const safeNear = near >= far ? Math.min(MIN_NEAR, far * 0.001) : near;
  const safeFar = Math.max(far, safeNear * 2);

  if (camera.near !== safeNear || camera.far !== safeFar) {
    camera.near = safeNear;
    camera.far = safeFar;
    camera.updateProjectionMatrix();
  }
}

export function getFitPlayerCameraPose(
  camera: PerspectiveCamera,
  root: Object3D,
  options?: FitPlayerCameraToCharacterOptions,
): FitPlayerCameraPose | null {
  const box = boundsForPlayerCharacter(root);
  return getFitPlayerCameraPoseForBox(camera, box, options);
}

/**
 * Positions perspective camera controls so the character fits in view, with optional vertical padding.
 */
export function fitPlayerCameraToCharacter(
  cameraControls: PlayerCameraControlsHandle,
  camera: PerspectiveCamera,
  root: Object3D,
  options?: FitPlayerCameraToCharacterOptions,
): boolean {
  const pose = getFitPlayerCameraPose(camera, root, options);
  if (!pose) return false;

  cameraControls.setLookAt(
    pose.position.x,
    pose.position.y,
    pose.position.z,
    pose.target.x,
    pose.target.y,
    pose.target.z,
    options?.enableTransition ?? false,
  );

  applyPlayerPerspectiveClippingForFraming(
    camera,
    pose.position,
    pose.target,
    pose.framingSphereRadius,
  );

  return true;
}
